/**
 * Purchasing & Suppliers.
 *
 * Buying stock is inventory, not an expense: a receipt raises stock at cost,
 * an invoice is what is owed, a payment settles it. None of them is ever an
 * expense row, so the cost reaches profit once — as cost of goods, when sold.
 *
 * Batch numbers and expiry dates are typed from the pack. The screen never
 * fills them in, and the API refuses a medicine received without them.
 */

import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Truck } from 'lucide-react';
import { PAYMENT_METHODS, PAYMENT_METHOD_LABELS } from '@cenox/shared';
import { api } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { useWorkingLocation } from '../../lib/locationContext';
import { date, money, todayIn } from '../../lib/format';
import { Button, Card, CardHeader, EmptyState, ErrorState, Input, Modal, Select, Spinner, Textarea, cn } from '../../components/ui';
import { PageHeader } from '../../layouts/AppShell';
import { newKey, usePharmacy, usePharmacyWrite, type BatchRow, type ListOf, type MedicineListItem, type UnitView } from './data';
import { ErrorLine, ExpiryBadge, StatusBadge, Table, Td } from './parts';

type Section = 'orders' | 'receive' | 'receipts' | 'invoices' | 'payments' | 'returns' | 'balances';
const SECTIONS: Array<{ key: Section; label: string }> = [
  { key: 'orders', label: 'Purchase orders' },
  { key: 'receive', label: 'Receive goods' },
  { key: 'receipts', label: 'Receipts' },
  { key: 'invoices', label: 'Invoices' },
  { key: 'payments', label: 'Payments' },
  { key: 'returns', label: 'Returns' },
  { key: 'balances', label: 'Balances' },
];

interface Supplier {
  id: string;
  name: string;
  isActive: boolean;
}

interface PoLine {
  id: string;
  product_id: string;
  product_name: string;
  unit_id: string;
  unit_name: string;
  quantity: number;
  base_quantity: number;
  unit_cost: string;
  received_base: number;
  outstandingBase: number;
}

interface PurchaseOrder {
  id: string;
  reference: string;
  supplier_id: string;
  supplier_name: string;
  branch_id: string;
  status: string;
  expected_on: string | null;
  lines: PoLine[];
}

interface DraftLine {
  productId: string;
  productName: string;
  tracksBatches: boolean;
  units: UnitView[];
  unitId: string;
  quantity: string;
  unitCost: string;
  batchNumber: string;
  expiryDate: string;
  poLineId: string | null;
  /** Optional, as printed on the pack. */
  manufacturedOn?: string;
  /** cool, refrigerated or frozen: the receiver confirms it is kept that way (0163). */
  storageRequirement?: string | undefined;
  storageConfirmed?: boolean;
}

const COLD_STORAGE = new Set(['cool', 'refrigerated', 'frozen']);

function useSuppliers() {
  const path = useBusinessPath();
  return useQuery({
    queryKey: ['pharmacy', path(''), 'suppliers-core'],
    queryFn: () => api.get<{ data: Supplier[] }>(path('/suppliers')),
  });
}

export function PurchasingPage(): ReactNode {
  const { can, business } = useAuth();
  const [section, setSection] = useState<Section>('orders');
  const [prefill, setPrefill] = useState<PurchaseOrder | null>(null);
  const c = business?.currencyCode ?? 'NGN';

  if (!can('pharmacy:purchasing')) {
    return <EmptyState title="Purchasing needs the purchasing permission" description="Ask the owner if your role should receive or order stock." />;
  }

  return (
    <div className="space-y-5">
      <PageHeader title="Purchasing & Suppliers" description="Orders, deliveries with batch and expiry, invoices, payments and what each supplier is owed" />
      <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
        Stock purchases raise inventory; they are not expenses. Their cost reaches profit as cost of goods when the stock is
        sold. Do not also record them under Expenses.
      </p>
      <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
        <div role="tablist" className="inline-flex gap-1 rounded-lg border border-slate-200 bg-white p-1">
          {SECTIONS.map((s) => (
            <button
              key={s.key}
              type="button"
              role="tab"
              aria-selected={section === s.key}
              onClick={() => setSection(s.key)}
              className={cn('whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium', section === s.key ? 'bg-brand-600 text-white' : 'text-slate-600 hover:bg-slate-50')}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {section === 'orders' && (
        <Orders
          currency={c}
          onReceive={(po) => {
            setPrefill(po);
            setSection('receive');
          }}
        />
      )}
      {section === 'receive' && <ReceiveForm key={prefill?.id ?? 'blank'} prefill={prefill} currency={c} onDone={() => setPrefill(null)} />}
      {section === 'receipts' && <Receipts currency={c} />}
      {section === 'invoices' && (
        <div className="space-y-5">
          <InvoiceForm />
          <Invoices currency={c} />
        </div>
      )}
      {section === 'payments' && <PaymentForm />}
      {section === 'returns' && <ReturnForm />}
      {section === 'balances' && <Balances currency={c} />}
    </div>
  );
}

// ── Shared pickers ─────────────────────────────────────────────────────────

function SupplierField({ value, onChange }: { value: string; onChange: (id: string) => void }): ReactNode {
  const { can } = useAuth();
  const path = useBusinessPath();
  const client = useQueryClient();
  const suppliers = useSuppliers();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function add() {
    setBusy(true);
    setError(null);
    try {
      const created = await api.post<Supplier>(path('/suppliers'), { name });
      await client.invalidateQueries({ queryKey: ['pharmacy'] });
      onChange(created.id);
      setAdding(false);
      setName('');
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <Select
            label="Supplier"
            required
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Choose a supplier"
            options={(suppliers.data?.data ?? []).filter((s) => s.isActive).map((s) => ({ value: s.id, label: s.name }))}
          />
        </div>
        {can('suppliers:manage') && (
          <Button type="button" variant="secondary" onClick={() => setAdding((v) => !v)}>
            {adding ? 'Close' : 'New'}
          </Button>
        )}
      </div>
      {adding && (
        <div className="flex items-end gap-2 rounded-lg border border-slate-200 p-3">
          <div className="flex-1">
            <Input label="Supplier name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <Button type="button" loading={busy} disabled={!name.trim()} onClick={() => void add()}>
            Add
          </Button>
        </div>
      )}
      <ErrorLine error={error} />
    </div>
  );
}

function LocationField({ value, onChange, label = 'Location' }: { value: string; onChange: (id: string) => void; label?: string }): ReactNode {
  const { locations } = useWorkingLocation();
  return (
    <Select
      label={label}
      required
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Choose a location"
      options={locations.map((l) => ({ value: l.id, label: l.name }))}
    />
  );
}

function useDefaultLocation(): string {
  const { branchId, defaultBranchId } = useWorkingLocation();
  return branchId ?? defaultBranchId ?? '';
}

function MedicinePicker({ onPick }: { onPick: (m: MedicineListItem) => void }): ReactNode {
  const [search, setSearch] = useState('');
  const results = usePharmacy<ListOf<MedicineListItem>>('/medicines', { search, limit: 8 }, search.trim().length >= 2);
  return (
    <div>
      <Input aria-label="Add an item" placeholder="Add an item — name, generic, SKU or barcode" value={search} onChange={(e) => setSearch(e.target.value)} />
      {search.trim().length >= 2 && (results.data?.data ?? []).length > 0 && (
        <ul className="mt-1 divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white">
          {results.data!.data.map((m) => (
            <li key={m.id}>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50"
                onClick={() => {
                  onPick(m);
                  setSearch('');
                }}
              >
                {m.name} <span className="text-slate-500">{[m.strength, m.dosageForm].filter(Boolean).join(' ')}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function toDraft(m: MedicineListItem): DraftLine {
  const purchasable = m.units.filter((u) => u.purchasable);
  const largest = [...purchasable].sort((a, b) => b.baseQuantity - a.baseQuantity)[0];
  return {
    productId: m.id,
    productName: m.name,
    tracksBatches: m.tracksBatches,
    units: purchasable,
    unitId: largest?.id ?? '',
    quantity: '',
    unitCost: '',
    batchNumber: '',
    expiryDate: '',
    poLineId: null,
    storageRequirement: m.storageRequirement,
    storageConfirmed: false,
  };
}

const wholePositive = (v: string) => /^\d+$/.test(v) && Number(v) > 0;

// ── Purchase orders ────────────────────────────────────────────────────────

function Orders({ currency, onReceive }: { currency: string; onReceive: (po: PurchaseOrder) => void }): ReactNode {
  const orders = usePharmacy<ListOf<{ id: string; reference: string; supplier_name: string; branch_name: string; status: string; expected_on: string | null; total: string }>>('/purchase-orders');
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<string | null>(null);

  return (
    <>
      <Card padded={false}>
        <div className="p-4 sm:p-5">
          <CardHeader
            title="Purchase orders"
            action={
              <Button icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
                New order
              </Button>
            }
          />
        </div>
        {orders.isError ? (
          <ErrorState message="Could not load orders." onRetry={() => void orders.refetch()} />
        ) : !orders.data ? (
          <Spinner label="Loading orders" />
        ) : orders.data.data.length === 0 ? (
          <EmptyState icon={<Truck className="size-8" />} title="No purchase orders yet" />
        ) : (
          <Table head={['Reference', 'Supplier', 'Location', 'Expected', 'Total', 'Status']} minWidth={640}>
            {orders.data.data.map((o) => (
              <tr key={o.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setOpen(o.id)}>
                <Td className="font-medium text-brand-700">{o.reference}</Td>
                <Td>{o.supplier_name}</Td>
                <Td>{o.branch_name}</Td>
                <Td>{o.expected_on ?? '—'}</Td>
                <Td className="tabular-nums">{money(o.total, currency)}</Td>
                <Td>
                  <StatusBadge status={o.status} />
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
      <NewOrderModal open={creating} onClose={() => setCreating(false)} currency={currency} />
      {open && <OrderDetail id={open} currency={currency} onClose={() => setOpen(null)} onReceive={onReceive} />}
    </>
  );
}

function NewOrderModal({ open, onClose, currency }: { open: boolean; onClose: () => void; currency: string }): ReactNode {
  const [supplierId, setSupplierId] = useState('');
  const [branchId, setBranchId] = useState(useDefaultLocation());
  const [expectedOn, setExpectedOn] = useState('');
  const [note, setNote] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [key] = useState(newKey);
  const create = usePharmacyWrite<Record<string, unknown>>('post', '/purchase-orders');
  const valid = supplierId && branchId && lines.length > 0 && lines.every((l) => l.unitId && wholePositive(l.quantity) && l.unitCost !== '');

  function submit(e: FormEvent) {
    e.preventDefault();
    create.mutate(
      {
        supplierId,
        branchId,
        expectedOn: expectedOn || null,
        note: note || null,
        lines: lines.map((l) => ({ productId: l.productId, unitId: l.unitId, quantity: Number(l.quantity), unitCost: l.unitCost })),
        idempotencyKey: key,
      },
      {
        onSuccess: () => {
          setLines([]);
          onClose();
        },
      },
    );
  }

  return (
    <Modal open={open} onClose={onClose} title="New purchase order" size="lg">
      <form onSubmit={submit} className="space-y-4">
        <SupplierField value={supplierId} onChange={setSupplierId} />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <LocationField value={branchId} onChange={setBranchId} label="Deliver to" />
          <Input label="Expected on" type="date" value={expectedOn} onChange={(e) => setExpectedOn(e.target.value)} />
        </div>
        <LineEditor lines={lines} setLines={setLines} currency={currency} withBatch={false} />
        <MedicinePicker onPick={(m) => setLines([...lines, toDraft(m)])} />
        <Textarea label="Note" value={note} onChange={(e) => setNote(e.target.value)} />
        <ErrorLine error={create.error} />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={create.isPending} disabled={!valid}>
            Raise order
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function OrderDetail({ id, currency, onClose, onReceive }: { id: string; currency: string; onClose: () => void; onReceive: (po: PurchaseOrder) => void }): ReactNode {
  const po = usePharmacy<PurchaseOrder>(`/purchase-orders/${id}`);
  const cancel = usePharmacyWrite<undefined>('post', `/purchase-orders/${id}/cancel`);
  const p = po.data;
  return (
    <Modal open onClose={onClose} title={p ? `${p.reference} · ${p.supplier_name}` : 'Purchase order'} size="lg">
      {!p ? (
        <Spinner label="Loading order" />
      ) : (
        <div className="space-y-4">
          <StatusBadge status={p.status} />
          <Table head={['Item', 'Ordered', 'Unit cost', 'Received', 'Outstanding']} minWidth={520}>
            {p.lines.map((l) => {
              const perUnit = l.base_quantity / l.quantity;
              return (
                <tr key={l.id}>
                  <Td className="font-medium">{l.product_name}</Td>
                  <Td className="tabular-nums">
                    {l.quantity} {l.unit_name}
                  </Td>
                  <Td className="tabular-nums">{money(l.unit_cost, currency)}</Td>
                  <Td className="tabular-nums">{l.received_base / perUnit} {l.unit_name}</Td>
                  <Td className="tabular-nums">{Math.max(0, l.outstandingBase) / perUnit} {l.unit_name}</Td>
                </tr>
              );
            })}
          </Table>
          <ErrorLine error={cancel.error} />
          <div className="flex flex-wrap justify-end gap-2">
            {['ordered', 'draft'].includes(p.status) && (
              <Button variant="ghost" loading={cancel.isPending} onClick={() => cancel.mutate(undefined, { onSuccess: onClose })}>
                Cancel order
              </Button>
            )}
            {['ordered', 'partially_received'].includes(p.status) && (
              <Button
                onClick={() => {
                  onReceive(p);
                  onClose();
                }}
              >
                Receive against this order
              </Button>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

// ── Lines ──────────────────────────────────────────────────────────────────

function LineEditor({
  lines,
  setLines,
  currency,
  withBatch,
}: {
  lines: DraftLine[];
  setLines: (next: DraftLine[]) => void;
  currency: string;
  withBatch: boolean;
}): ReactNode {
  const update = (i: number, patch: Partial<DraftLine>) => setLines(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  if (lines.length === 0) return <p className="text-sm text-slate-500">No items yet — search below to add one.</p>;
  return (
    <div className="space-y-2">
      {lines.map((l, i) => {
        const unit = l.units.find((u) => u.id === l.unitId);
        return (
          <div key={i} className="rounded-lg border border-slate-200 p-3">
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="text-sm font-medium">{l.productName}</span>
              <button type="button" aria-label="Remove line" className="text-slate-400 hover:text-red-600" onClick={() => setLines(lines.filter((_, j) => j !== i))}>
                <Trash2 className="size-4" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              <Select
                label="Unit"
                value={l.unitId}
                onChange={(e) => update(i, { unitId: e.target.value })}
                options={l.units.map((u) => ({ value: u.id, label: u.isBase ? u.name : `${u.name} (${u.baseQuantity})` }))}
              />
              <Input
                label="Quantity"
                type="number"
                min={1}
                step={1}
                inputMode="numeric"
                value={l.quantity}
                error={l.quantity && !wholePositive(l.quantity) ? 'Whole units only' : undefined}
                onChange={(e) => update(i, { quantity: e.target.value })}
              />
              <Input label={`Cost per ${unit?.name ?? 'unit'}`} prefix={currency} inputMode="decimal" value={l.unitCost} onChange={(e) => update(i, { unitCost: e.target.value })} />
              {withBatch && (
                <>
                  <Input
                    label="Batch number"
                    required={l.tracksBatches}
                    value={l.batchNumber}
                    onChange={(e) => update(i, { batchNumber: e.target.value })}
                  />
                  <Input
                    label="Expiry date"
                    type="date"
                    required={l.tracksBatches}
                    value={l.expiryDate}
                    onChange={(e) => update(i, { expiryDate: e.target.value })}
                  />
                  <Input
                    label="Made on (optional)"
                    type="date"
                    value={l.manufacturedOn ?? ''}
                    onChange={(e) => update(i, { manufacturedOn: e.target.value })}
                  />
                  {unit && wholePositive(l.quantity) && (
                    <p className="self-end pb-2 text-xs text-slate-500">= {Number(l.quantity) * unit.baseQuantity} {l.units.find((u) => u.isBase)?.name ?? 'base units'}</p>
                  )}
                </>
              )}
            </div>
            {/* Cold: the receiver says it is kept so. */}
            {withBatch && l.storageRequirement !== undefined && COLD_STORAGE.has(l.storageRequirement) && (
              <label className="mt-2 flex items-start gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  className="mt-0.5 size-4 rounded border-slate-300 text-brand-600"
                  checked={l.storageConfirmed === true}
                  onChange={(e) => update(i, { storageConfirmed: e.target.checked })}
                />
                This delivery is kept {l.storageRequirement} as its storage requires
              </label>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Receive ────────────────────────────────────────────────────────────────

function ReceiveForm({ prefill, currency, onDone }: { prefill: PurchaseOrder | null; currency: string; onDone: () => void }): ReactNode {
  const { business } = useAuth();
  const fallback = useDefaultLocation();
  const [supplierId, setSupplierId] = useState(prefill?.supplier_id ?? '');
  const [branchId, setBranchId] = useState(prefill?.branch_id ?? fallback);
  const [receivedOn, setReceivedOn] = useState(todayIn(business?.timezone ?? 'UTC'));
  const [note, setNote] = useState('');
  const [key, setKey] = useState(newKey);
  const [lines, setLines] = useState<DraftLine[]>(() =>
    (prefill?.lines ?? [])
      .filter((l) => l.outstandingBase > 0)
      .map((l) => {
        const perUnit = l.base_quantity / l.quantity;
        return {
          productId: l.product_id,
          productName: l.product_name,
          tracksBatches: true,
          units: [{ id: l.unit_id, name: l.unit_name, baseQuantity: perUnit, barcode: null, price: null, isBase: perUnit === 1, sellable: true, purchasable: true }],
          unitId: l.unit_id,
          quantity: String(Math.floor(l.outstandingBase / perUnit)),
          unitCost: l.unit_cost,
          batchNumber: '',
          expiryDate: '',
          poLineId: l.id,
        };
      }),
  );
  const receive = usePharmacyWrite<Record<string, unknown>, { reference: string; total: string }>('post', '/receipts');
  /*
    A line from an order does not carry its item's storage. Read it, so a cool,
    fridge or freezer item asks for its confirmation before anything is sent.
  */
  const path = useBusinessPath();
  const unknownStorage = lines.filter((l) => l.storageRequirement === undefined).map((l) => l.productId).join(',');
  useEffect(() => {
    if (!unknownStorage) return;
    let live = true;
    void Promise.all(
      [...new Set(unknownStorage.split(','))].map(async (id) => {
        const m = await api.get<{ storageRequirement: string }>(path(`/pharmacy/medicines/${id}`));
        return [id, m.storageRequirement] as const;
      }),
    ).then((found) => {
      if (!live) return;
      const by = new Map(found);
      setLines((all) => all.map((l) => (l.storageRequirement === undefined && by.has(l.productId) ? { ...l, storageRequirement: by.get(l.productId) } : l)));
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unknownStorage]);
  const valid =
    supplierId &&
    branchId &&
    lines.length > 0 &&
    lines.every(
      (l) =>
        l.unitId &&
        wholePositive(l.quantity) &&
        l.unitCost !== '' &&
        (!l.tracksBatches || (l.batchNumber.trim() && l.expiryDate)) &&
        // Storage known (read for lines from an order), and confirmed when cold.
        l.storageRequirement !== undefined &&
        (!COLD_STORAGE.has(l.storageRequirement) || l.storageConfirmed === true),
    );

  function submit(e: FormEvent) {
    e.preventDefault();
    receive.mutate(
      {
        supplierId,
        branchId,
        purchaseOrderId: prefill?.id ?? null,
        receivedOn,
        note: note || null,
        lines: lines.map((l) => ({
          poLineId: l.poLineId,
          productId: l.productId,
          unitId: l.unitId,
          quantity: Number(l.quantity),
          unitCost: l.unitCost,
          batchNumber: l.batchNumber.trim() || null,
          expiryDate: l.expiryDate || null,
          manufacturedOn: l.manufacturedOn || null,
          storageConfirmed: l.storageConfirmed === true,
        })),
        post: true,
        idempotencyKey: key,
      },
      {
        onSuccess: () => {
          setLines([]);
          setKey(newKey());
          onDone();
        },
      },
    );
  }

  return (
    <Card>
      <CardHeader title="Receive goods" subtitle={prefill ? `Against ${prefill.reference}` : 'A delivery, with or without an order'} />
      <form onSubmit={submit} className="space-y-4">
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Batch number and expiry date are required for every medicine. Read them from the pack — CenoX never fills them in.
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="sm:col-span-1">
            <SupplierField value={supplierId} onChange={setSupplierId} />
          </div>
          <LocationField value={branchId} onChange={setBranchId} label="Received at" />
          <Input label="Received on" type="date" value={receivedOn} onChange={(e) => setReceivedOn(e.target.value)} />
        </div>
        <LineEditor lines={lines} setLines={setLines} currency={currency} withBatch />
        {!prefill && <MedicinePicker onPick={(m) => setLines([...lines, toDraft(m)])} />}
        <Textarea label="Note" value={note} onChange={(e) => setNote(e.target.value)} />
        <ErrorLine error={receive.error} />
        {receive.data && (
          <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
            {receive.data.reference} posted — {money(receive.data.total, currency)} into stock.
          </p>
        )}
        <div className="flex justify-end">
          <Button type="submit" loading={receive.isPending} disabled={!valid}>
            Post receipt
          </Button>
        </div>
      </form>
    </Card>
  );
}

function Receipts({ currency }: { currency: string }): ReactNode {
  const { business } = useAuth();
  const receipts = usePharmacy<ListOf<{ id: string; reference: string; supplier_name: string; branch_name: string; status: string; received_on: string; total: string }>>('/receipts');
  return (
    <Card padded={false}>
      <div className="p-4 sm:p-5">
        <CardHeader title="Receipts" subtitle="Goods received notes" />
      </div>
      {!receipts.data ? (
        <Spinner label="Loading receipts" />
      ) : receipts.data.data.length === 0 ? (
        <EmptyState title="No deliveries received yet" />
      ) : (
        <Table head={['Reference', 'Supplier', 'Location', 'Received', 'Total', 'Status']} minWidth={620}>
          {receipts.data.data.map((r) => (
            <tr key={r.id}>
              <Td className="font-medium">{r.reference}</Td>
              <Td>{r.supplier_name}</Td>
              <Td>{r.branch_name}</Td>
              <Td>{date(r.received_on, business?.timezone ?? 'UTC')}</Td>
              <Td className="tabular-nums">{money(r.total, currency)}</Td>
              <Td>
                <StatusBadge status={r.status} />
              </Td>
            </tr>
          ))}
        </Table>
      )}
    </Card>
  );
}

// ── Invoices, payments, returns, balances ──────────────────────────────────

function InvoiceForm(): ReactNode {
  const { business } = useAuth();
  const [form, setForm] = useState({ supplierId: '', branchId: useDefaultLocation(), invoiceNumber: '', invoiceDate: todayIn(business?.timezone ?? 'UTC'), dueDate: '', amount: '', note: '' });
  const write = usePharmacyWrite<Record<string, unknown>, { id: string; duplicate: boolean }>('post', '/supplier-invoices');
  return (
    <Card>
      <CardHeader title="Supplier invoice" subtitle="What the business owes. Entering the same invoice twice records it once." />
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          write.mutate({ ...form, dueDate: form.dueDate || null, note: form.note || null });
        }}
      >
        <SupplierField value={form.supplierId} onChange={(supplierId) => setForm({ ...form, supplierId })} />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <LocationField value={form.branchId} onChange={(branchId) => setForm({ ...form, branchId })} />
          <Input label="Invoice number" required value={form.invoiceNumber} onChange={(e) => setForm({ ...form, invoiceNumber: e.target.value })} />
          <Input label="Invoice date" type="date" required value={form.invoiceDate} onChange={(e) => setForm({ ...form, invoiceDate: e.target.value })} />
          <Input label="Due date" type="date" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} />
          <Input label="Amount" required inputMode="decimal" prefix={business?.currencyCode} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
        </div>
        <Textarea label="Note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
        <ErrorLine error={write.error} />
        {write.data && <p className="text-sm text-emerald-700">{write.data.duplicate ? 'Already recorded — nothing added.' : 'Invoice recorded.'}</p>}
        <div className="flex justify-end">
          <Button type="submit" loading={write.isPending} disabled={!form.supplierId || !form.invoiceNumber || !form.amount}>
            Record invoice
          </Button>
        </div>
      </form>
    </Card>
  );
}

interface SupplierInvoiceRow {
  id: string;
  supplier_id: string;
  supplier_name: string;
  invoice_number: string;
  invoice_date: string;
  due_date: string | null;
  amount: string;
  status: string;
  paid: string;
  outstanding: string;
}

function Invoices({ currency }: { currency: string }): ReactNode {
  const invoices = usePharmacy<ListOf<SupplierInvoiceRow>>('/supplier-invoices');
  return (
    <Card padded={false}>
      <div className="p-4 sm:p-5">
        <CardHeader title="Invoices" subtitle="What each supplier billed, and how much of it is still owed" />
      </div>
      {invoices.isError ? (
        <ErrorState message="Could not load invoices." onRetry={() => void invoices.refetch()} />
      ) : !invoices.data ? (
        <Spinner label="Loading invoices" />
      ) : invoices.data.data.length === 0 ? (
        <EmptyState title="No supplier invoices yet" />
      ) : (
        <Table head={['Number', 'Supplier', 'Date', 'Due', 'Amount', 'Paid', 'Outstanding', 'Status']} minWidth={760}>
          {invoices.data.data.map((i) => (
            <tr key={i.id}>
              <Td className="font-medium">{i.invoice_number}</Td>
              <Td>{i.supplier_name}</Td>
              <Td className="tabular-nums">{i.invoice_date}</Td>
              <Td className="tabular-nums">{i.due_date ?? '—'}</Td>
              <Td className="tabular-nums">{money(i.amount, currency)}</Td>
              <Td className="tabular-nums">{money(i.paid, currency)}</Td>
              <Td className={cn('font-semibold tabular-nums', Number(i.outstanding) > 0 ? 'text-amber-700' : 'text-slate-900')}>{money(i.outstanding, currency)}</Td>
              <Td><StatusBadge status={i.status} /></Td>
            </tr>
          ))}
        </Table>
      )}
    </Card>
  );
}

function PaymentForm(): ReactNode {
  const { business } = useAuth();
  const [key, setKey] = useState(newKey);
  const [form, setForm] = useState({ supplierId: '', branchId: useDefaultLocation(), supplierInvoiceId: '', amount: '', method: 'bank_transfer', reference: '', paidOn: todayIn(business?.timezone ?? 'UTC'), note: '' });
  const write = usePharmacyWrite<Record<string, unknown>, { id: string; duplicate: boolean }>('post', '/supplier-payments');
  const openInvoices = usePharmacy<ListOf<SupplierInvoiceRow>>(
    '/supplier-invoices',
    { supplierId: form.supplierId, openOnly: true },
    Boolean(form.supplierId),
  );
  return (
    <Card>
      <CardHeader title="Pay a supplier" subtitle="Settles what is owed. Never an expense." />
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          write.mutate(
            {
              ...form,
              supplierInvoiceId: form.supplierInvoiceId || null,
              reference: form.reference || null,
              note: form.note || null,
              idempotencyKey: key,
            },
            { onSuccess: () => setKey(newKey()) },
          );
        }}
      >
        <SupplierField value={form.supplierId} onChange={(supplierId) => setForm({ ...form, supplierId, supplierInvoiceId: '' })} />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <LocationField value={form.branchId} onChange={(branchId) => setForm({ ...form, branchId })} label="Paid from" />
          <Input label="Amount" required inputMode="decimal" prefix={business?.currencyCode} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          <Select
            label="Method"
            value={form.method}
            onChange={(e) => setForm({ ...form, method: e.target.value })}
            options={PAYMENT_METHODS.filter((m) => m !== 'credit').map((m) => ({ value: m, label: PAYMENT_METHOD_LABELS[m] }))}
          />
          <Input label="Paid on" type="date" value={form.paidOn} onChange={(e) => setForm({ ...form, paidOn: e.target.value })} />
          <Input label="Reference" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
          <Select
            label="Against invoice (optional)"
            value={form.supplierInvoiceId}
            placeholder={form.supplierId ? 'Not against a specific invoice' : 'Choose the supplier first'}
            disabled={!form.supplierId}
            onChange={(e) => setForm({ ...form, supplierInvoiceId: e.target.value })}
            options={(openInvoices.data?.data ?? [])
              .filter((i) => Number(i.outstanding) > 0)
              .map((i) => ({ value: i.id, label: `${i.invoice_number} · ${i.invoice_date} · ${money(i.outstanding, business?.currencyCode ?? 'NGN')} owed` }))}
          />
        </div>
        <ErrorLine error={write.error} />
        {write.data && <p className="text-sm text-emerald-700">{write.data.duplicate ? 'Already recorded — nothing added.' : 'Payment recorded.'}</p>}
        <div className="flex justify-end">
          <Button type="submit" loading={write.isPending} disabled={!form.supplierId || !form.amount}>
            Record payment
          </Button>
        </div>
      </form>
    </Card>
  );
}

function ReturnForm(): ReactNode {
  const { business } = useAuth();
  const [key, setKey] = useState(newKey);
  const [form, setForm] = useState({ supplierId: '', branchId: useDefaultLocation(), batchId: '', baseQuantity: '', creditAmount: '', reason: '' });
  const batches = usePharmacy<ListOf<BatchRow>>('/batches', { branchId: form.branchId }, Boolean(form.branchId));
  const write = usePharmacyWrite<Record<string, unknown>, { reference: string }>('post', '/supplier-returns');
  const batch = batches.data?.data.find((b) => b.batchId === form.batchId);
  return (
    <Card>
      <CardHeader title="Return to supplier" subtitle="Stock leaves the shelf; the credit reduces what is owed" />
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          write.mutate(
            { ...form, baseQuantity: Number(form.baseQuantity), idempotencyKey: key },
            {
              onSuccess: () => {
                setKey(newKey());
                setForm({ ...form, batchId: '', baseQuantity: '', creditAmount: '', reason: '' });
              },
            },
          );
        }}
      >
        <SupplierField value={form.supplierId} onChange={(supplierId) => setForm({ ...form, supplierId })} />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <LocationField value={form.branchId} onChange={(branchId) => setForm({ ...form, branchId, batchId: '' })} label="From" />
          <Select
            label="Batch"
            required
            value={form.batchId}
            onChange={(e) => setForm({ ...form, batchId: e.target.value })}
            placeholder="Choose a batch"
            options={(batches.data?.data ?? [])
              .filter((b) => b.onHand > 0)
              .map((b) => ({ value: b.batchId, label: `${b.productName ?? ''} · ${b.batchNumber} · exp ${b.expiryDate} · ${b.onHand} on hand` }))}
          />
          <Input
            label="Quantity (base units)"
            type="number"
            min={1}
            step={1}
            required
            hint={batch ? `${batch.onHand} on hand` : undefined}
            value={form.baseQuantity}
            onChange={(e) => setForm({ ...form, baseQuantity: e.target.value })}
          />
          <Input label="Credit from supplier" required inputMode="decimal" prefix={business?.currencyCode} value={form.creditAmount} onChange={(e) => setForm({ ...form, creditAmount: e.target.value })} />
        </div>
        {batch && (
          <p className="text-xs text-slate-500">
            Expiry <ExpiryBadge expiryDate={batch.expiryDate} expired={batch.expired} nearExpiry={batch.nearExpiry} />
          </p>
        )}
        <Textarea label="Reason" required value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
        <ErrorLine error={write.error} />
        {write.data && <p className="text-sm text-emerald-700">Return {write.data.reference} recorded.</p>}
        <div className="flex justify-end">
          <Button
            type="submit"
            loading={write.isPending}
            disabled={!form.supplierId || !form.batchId || !wholePositive(form.baseQuantity) || form.creditAmount === '' || form.reason.trim().length < 3}
          >
            Record return
          </Button>
        </div>
      </form>
    </Card>
  );
}

function Balances({ currency }: { currency: string }): ReactNode {
  const balances = usePharmacy<ListOf<{ supplierId: string; name: string; opening: string; invoiced: string; paid: string; credits: string; balance: string }>>('/supplier-balances');
  return (
    <Card padded={false}>
      <div className="p-4 sm:p-5">
        <CardHeader title="Supplier balances" subtitle="Opening balance + invoices − payments − return credits" />
      </div>
      {!balances.data ? (
        <Spinner label="Loading balances" />
      ) : balances.data.data.length === 0 ? (
        <EmptyState title="No suppliers yet" />
      ) : (
        <Table head={['Supplier', 'Opening', 'Invoiced', 'Paid', 'Credits', 'Balance']} minWidth={640}>
          {balances.data.data.map((b) => (
            <tr key={b.supplierId}>
              <Td className="font-medium">{b.name}</Td>
              <Td className="tabular-nums">{money(b.opening, currency)}</Td>
              <Td className="tabular-nums">{money(b.invoiced, currency)}</Td>
              <Td className="tabular-nums">{money(b.paid, currency)}</Td>
              <Td className="tabular-nums">{money(b.credits, currency)}</Td>
              <Td className={cn('font-semibold tabular-nums', Number(b.balance) > 0 ? 'text-amber-700' : 'text-slate-900')}>{money(b.balance, currency)}</Td>
            </tr>
          ))}
        </Table>
      )}
    </Card>
  );
}
